<div class="flex flex-col flex-1 px-3 py-2 max-w-5xl mx-auto w-full">
  <div class="flex items-center mb-2">
    <h1 class="text-base font-semibold">AI Settings</h1>
    <div class="ml-auto flex gap-2">
      <button class="btn btn-sm btn-outline" (click)="load()" [disabled]="loading">
        <i class="ri-refresh-line"></i>
        Refresh
      </button>
    </div>
  </div>
  <p class="text-sm text-gray-500 mb-3">
    Global LLM configuration for all AI features (AI Ticket, AI Evaluation). Vertex AI engine.
    Changes apply live — no redeploy.
  </p>

  @if (loadError) {
    <div class="alert alert-error text-sm mb-3">{{ loadError }}</div>
  }

  @if (!loading) {
    <!-- ===== Active configuration ===== -->
    <div class="card border border-gray-200 rounded-lg mb-4">
      <div class="px-4 py-3 border-b border-gray-200 flex items-center gap-2">
        <h2 class="text-sm font-semibold">Active configuration</h2>
        @if (effective) {
          <span
            class="badge badge-sm"
            [ngClass]="effective.source === 'db' ? 'badge-success' : 'badge-ghost'"
          >
            {{
              effective.source === 'db'
                ? 'admin-saved'
                : effective.source === 'env'
                  ? 'helm bootstrap'
                  : 'binary default'
            }}
          </span>
        }
        @if (settings?.updated_by) {
          <span class="ml-auto text-xs text-gray-500">
            last changed by
            <b>{{ settings?.updated_by }}</b>
            · {{ settings?.updated_at | date: 'short' }}
          </span>
        }
      </div>
      <div class="p-4">
        @if (isEuRegion) {
          <div class="alert text-sm mb-4 bg-green-50 text-green-800 border-green-200">
            ✓
            <b>EU data residency OK.</b>
            <code>{{ region }}</code>
            keeps session traces in the EU — consistent with the DLP / replay vault region.
          </div>
        } @else if (region) {
          <div class="alert text-sm mb-4 bg-amber-50 text-amber-800 border-amber-200">
            ⚠
            <b>Data leaves the EU.</b>
            <code>{{ region }}</code>
            is outside Europe. AI Ticket / AI Evaluation send the full session trace (may contain
            PII) to this region. Your DLP/vault posture is EU — change region or confirm this is
            intended.
          </div>
        }

        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label class="block text-xs font-semibold text-gray-500 uppercase mb-1">Engine</label>
            <select class="select select-sm select-bordered w-full" disabled>
              <option>Vertex AI (Google Cloud)</option>
            </select>
          </div>
          <div>
            <label class="block text-xs font-semibold text-gray-500 uppercase mb-1">
              Region (Vertex location)
            </label>
            <select
              class="select select-sm select-bordered w-full"
              [(ngModel)]="region"
              (ngModelChange)="onRegionChange()"
            >
              <optgroup label="🇪🇺 Europe (recommended — EU residency)">
                @for (r of regionsByGroup('eu'); track r.value) {
                  <option [value]="r.value">{{ r.label }}</option>
                }
              </optgroup>
              <optgroup label="🌍 Multi-region">
                @for (r of regionsByGroup('multi'); track r.value) {
                  <option [value]="r.value">{{ r.label }}</option>
                }
              </optgroup>
              <optgroup label="🇺🇸/🌏 Non-EU (warns)">
                @for (r of regionsByGroup('non-eu'); track r.value) {
                  <option [value]="r.value">{{ r.label }}</option>
                }
              </optgroup>
            </select>
            <p class="text-xs text-gray-500 mt-1">
              Model list is filtered to what's GA in this region.
            </p>
          </div>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
          <div>
            <label class="block text-xs font-semibold text-gray-500 uppercase mb-1">Model</label>
            <select
              class="select select-sm select-bordered w-full"
              [(ngModel)]="modelId"
              (ngModelChange)="onModelChange()"
            >
              <option value="">— select —</option>
              @for (pub of publishers; track pub) {
                <optgroup [label]="pub">
                  @for (m of modelsForPublisher(pub); track m.model_id) {
                    <option [value]="m.model_id">
                      {{ m.display_name }}{{ m.supports_json_schema ? '' : ' · ⚠ no JSON-schema' }}
                    </option>
                  }
                </optgroup>
              }
            </select>
            <p class="text-xs text-gray-500 mt-1">
              Grouped by publisher; only models GA in
              <b>{{ region || '—' }}</b>
              .
            </p>
          </div>
          <div>
            <label class="block text-xs font-semibold text-gray-500 uppercase mb-1">
              Model version
            </label>
            <select class="select select-sm select-bordered w-full" [(ngModel)]="modelVersion">
              @for (v of selectedModel?.versions || []; track v) {
                <option [value]="v">{{ v }}</option>
              }
            </select>
          </div>
        </div>

        @if (modelId && !jsonCapable) {
          <div class="alert text-sm mt-3 bg-red-50 text-red-800 border-red-200">
            ⛔
            <b>This model can't do structured JSON output.</b>
            AI Ticket &amp; AI Evaluation require
            <code>responseSchema</code>
            +
            <code>application/json</code>
            controlled generation. Saving it will make those features fail — pick a
            JSON-schema-capable model.
          </div>
        }

        <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
          <div>
            <label class="block text-xs font-semibold text-gray-500 uppercase mb-1">
              Request timeout (seconds)
            </label>
            <input
              type="number"
              min="5"
              max="600"
              class="input input-sm input-bordered w-full"
              [(ngModel)]="timeoutSeconds"
            />
          </div>
          <div>
            <label class="block text-xs font-semibold text-gray-500 uppercase mb-1">
              Effective resolution
            </label>
            <p class="text-xs text-gray-500 border-l-2 border-gray-200 pl-3">
              DB setting (this page) ➜
              <code>TRACER_VERTEX_*</code>
              env (helm bootstrap) ➜ binary defaults. Saving here overrides the env at runtime — no
              redeploy.
            </p>
          </div>
        </div>

        <div class="flex items-center gap-3 mt-4">
          <button
            class="btn btn-sm btn-outline"
            (click)="test()"
            [disabled]="testing || !region || !modelId"
          >
            @if (testing) {
              <span class="loading loading-spinner loading-xs"></span>
            }
            Test selected model
          </button>
          <span class="text-xs text-gray-500">
            Fires one tiny Vertex call with this exact model + region.
          </span>
          @if (hasUnsavedChanges()) {
            <span class="badge badge-sm badge-ghost gap-1 ml-auto" title="You have unsaved edits">
              <i class="ri-error-warning-line"></i>
              Unsaved changes
            </span>
          }
          <button
            class="btn btn-sm btn-primary"
            [ngClass]="hasUnsavedChanges() ? '' : 'ml-auto'"
            (click)="save()"
            [disabled]="!canSave"
          >
            @if (saving) {
              <span class="loading loading-spinner loading-xs"></span>
            }
            Save configuration
          </button>
        </div>

        @if (saveError) {
          <div class="alert alert-error text-sm mt-3">{{ saveError }}</div>
        }
        @if (saveOk) {
          <div class="alert text-sm mt-3 bg-green-50 text-green-800 border-green-200">
            ✓ Saved. New configuration is now in force for all AI features.
          </div>
        }

        @if (testResult) {
          <div
            class="mt-3 rounded-lg p-3 text-xs font-mono whitespace-pre-wrap"
            [ngClass]="testResult.ok ? 'bg-gray-900 text-green-300' : 'bg-gray-900 text-red-300'"
          >
            ▶ test {{ testResult.region }} / {{ testResult.model }}
            {{ '\n' }}
            @if (testResult.ok) {
              ✓ OK — reachable in {{ testResult.region }} ({{ testResult.latency_ms }} ms).
            } @else {
              ✕ {{ testResult.error }}
            }
          </div>
        }
      </div>
    </div>

    <!-- ===== Model catalog ===== -->
    <div class="card border border-gray-200 rounded-lg mb-4">
      <div class="px-4 py-3 border-b border-gray-200 flex items-center gap-2">
        <h2 class="text-sm font-semibold">Model catalog</h2>
        <span class="badge badge-sm badge-ghost">auto-synced</span>
        <span class="ml-auto text-xs text-gray-500">
          {{ models.length }} models
          @if (lastSynced) {
            · last sync {{ lastSynced | date: 'short' }}
          }
          · daily
        </span>
        <button class="btn btn-xs btn-outline" (click)="syncNow()" [disabled]="syncing">
          @if (syncing) {
            <span class="loading loading-spinner loading-xs"></span>
          }
          Sync now
        </button>
      </div>
      <div class="p-4 overflow-x-auto">
        @if (syncError) {
          <div class="alert alert-error text-sm mb-2">{{ syncError }}</div>
        }
        <table class="table table-xs">
          <thead>
            <tr>
              <th>Publisher</th>
              <th>Model</th>
              <th>Versions</th>
              <th>GA regions</th>
              <th>JSON schema</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            @for (m of models; track m.model_id) {
              <tr [ngClass]="{ 'bg-red-50': m.model_id === modelId }">
                <td>{{ m.publisher }}</td>
                <td>
                  <b>{{ m.display_name }}</b>
                  <div class="text-gray-400">{{ m.model_id }}</div>
                </td>
                <td>{{ m.versions.join(', ') }}</td>
                <td class="max-w-xs">{{ m.regions.join(', ') }}</td>
                <td>
                  @if (m.supports_json_schema) {
                    <span class="badge badge-xs badge-success">✓</span>
                  } @else {
                    <span class="badge badge-xs badge-error">✕</span>
                  }
                </td>
                <td>
                  <span
                    class="badge badge-xs"
                    [ngClass]="m.status === 'ga' ? 'badge-success' : 'badge-warning'"
                  >
                    {{ m.status }}
                  </span>
                </td>
              </tr>
            }
          </tbody>
        </table>
        <p class="text-xs text-gray-500 mt-3">
          Curated baseline ships in code (never empty); the daily sync re-asserts it and stamps the
          timestamp.
          <span class="badge badge-xs badge-success">✓</span>
          = usable by AI Ticket/Eval;
          <span class="badge badge-xs badge-error">✕</span>
          = text-only (would break them — selection is blocked).
        </p>
      </div>
    </div>
  } @else {
    <div class="flex justify-center py-10">
      <span class="loading loading-spinner loading-md"></span>
    </div>
  }
</div>
